import styled, { keyframes } from "styled-components";

const slideIn = keyframes`
  from { transform: translateX(100%); }
  to   { transform: translateX(0); }
`;

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.3);
  z-index: 300;
  display: flex;
  justify-content: flex-end;
`;

export const Drawer = styled.div`
  width: 520px;
  height: 100%;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  animation: ${slideIn} 0.22s ease;
  box-shadow: -4px 0 32px rgba(0, 0, 0, 0.12);
`;

export const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 28px;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
`;

export const DrawerTitle = styled.h2`
  font-size: 18px;
  font-weight: 800;
  color: #111;
  letter-spacing: -0.02em;
`;

export const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background-color: #f5f5f5;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #555;
  transition: background-color 0.15s;

  &:hover { background-color: #ebebeb; }
`;

export const DrawerBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px 28px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const DrawerFooter = styled.div`
  padding: 20px 28px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  gap: 10px;
  flex-shrink: 0;
`;

/* ─── Form elements ──────────────────────────────────────────── */

export const Field = styled.div``;

export const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

export const Label = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #555;
  letter-spacing: 0.02em;
  margin-bottom: 8px;
`;

export const Required = styled.span`
  color: #fc324a;
  margin-left: 2px;
`;

export const Input = styled.input<{ hasError?: boolean }>`
  width: 100%;
  height: 44px;
  padding: 0 12px;
  border: 1.5px solid ${({ hasError }) => (hasError ? "#fc324a" : "#e8e8e8")};
  border-radius: 10px;
  font-size: 14px;
  color: #111;
  background-color: #fafafa;
  transition: border-color 0.15s;

  &::placeholder { color: #bbb; }
  &:focus {
    outline: none;
    border-color: ${({ hasError }) => (hasError ? "#fc324a" : "#1fbc70")};
    background-color: #fff;
  }
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button { -webkit-appearance: none; }
`;

export const FieldError = styled.p`
  font-size: 12px;
  color: #fc324a;
  margin-top: 5px;
`;

/* ─── Image upload ───────────────────────────────────────────── */

export const ImageUploadBox = styled.label<{ hasError?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 100px;
  border: 1.5px dashed ${({ hasError }) => (hasError ? "#fc324a" : "#ddd")};
  border-radius: 12px;
  cursor: pointer;
  background-color: #fafafa;
  transition: border-color 0.15s, background-color 0.15s;

  &:hover {
    border-color: #1fbc70;
    background-color: #f0fdf7;
  }

  input { display: none; }
`;

export const UploadIcon = styled.span`
  font-size: 24px;
`;

export const UploadText = styled.p`
  font-size: 13px;
  color: #aaa;
  text-align: center;
`;

export const UploadedName = styled.p`
  font-size: 13px;
  font-weight: 600;
  color: #1fbc70;
  text-align: center;
`;

export const MultiImageList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 8px;
`;

export const MultiImageRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background-color: #f5f5f5;
  border-radius: 8px;
`;

export const MultiImageName = styled.span`
  font-size: 13px;
  color: #444;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  margin-right: 8px;
`;

export const RemoveImageBtn = styled.button`
  font-size: 12px;
  color: #fc324a;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  font-weight: 600;

  &:hover { text-decoration: underline; }
`;

export const AddImageBtn = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #1fbc70;
  cursor: pointer;

  input { display: none; }
  &:hover { text-decoration: underline; }
`;

/* ─── Operation times ────────────────────────────────────────── */

export const SectionLabel = styled.p`
  font-size: 13px;
  font-weight: 700;
  color: #333;
  margin-bottom: 10px;
`;

export const OpTimeList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const OpTimeRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr auto;
  gap: 8px;
  align-items: start;
`;

export const RemoveOpBtn = styled.button`
  width: 36px;
  height: 44px;
  border-radius: 8px;
  border: 1.5px solid #f0d0d0;
  background-color: #fff5f5;
  color: #fc324a;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.15s;

  &:hover { background-color: #ffe0e0; }
`;

export const AddOpBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 14px;
  border: 1.5px dashed #1fbc70;
  border-radius: 8px;
  background: none;
  color: #1fbc70;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s;
  width: fit-content;

  &:hover { background-color: #f0fdf7; }
`;

/* ─── Footer buttons ─────────────────────────────────────────── */

export const CancelButton = styled.button`
  flex: 1;
  height: 48px;
  border: 1.5px solid #e8e8e8;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  color: #666;
  background-color: #fff;
  cursor: pointer;
  transition: background-color 0.15s;

  &:hover { background-color: #f5f5f5; }
`;

export const SubmitButton = styled.button`
  flex: 2;
  height: 48px;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 700;
  color: #fff;
  background-color: #1fbc70;
  cursor: pointer;
  transition: background-color 0.15s;

  &:hover { background-color: #18a860; }
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;
